/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ViewRegistrationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import styles from "./index.module.css";

import { ReactComponent as CalendarIcon } from "../../assets/icons/ViewRegistrationModal/CalendarIcon.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/ViewRegistrationModal/ClockIcon.svg";
import CloseIcon from "@mui/icons-material/Close";

const ViewRegistrationModal = ({ open, onClose, event }) => {
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  useEffect(() => {
    const generateQR = async () => {
      if (event) {
        const userId = localStorage.getItem("userId");
        const qrData = {
          userId,
          eventId: event._id,
        };

        const dataString = JSON.stringify(qrData);

        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(dataString)}`;
        setQrCodeUrl(qrUrl);
      }
    };

    generateQR();
  }, [event]);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>

        <div className={styles.modalHeader}>
          <h2>{event?.nombre}</h2>
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <CalendarIcon className={styles.icon} />
              <span>{t("components.viewRegistrationModal.eventDate")}:</span>
            </div>
            <div className={styles.infoValue}>
              {dayjs(event?.fechaEvento).utc().format("LL")}
            </div>
          </div>

          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <ClockIcon className={styles.icon} />
              <span>{t("components.viewRegistrationModal.eventTime")}:</span>
            </div>
            <div className={styles.infoValue}>{event?.hora}</div>
          </div>
        </div>

        <div className={styles.qrContainer}>
          {qrCodeUrl && (
            <img
              src={qrCodeUrl}
              alt={t("common.qrCodeAlt")}
              className={styles.qrCode}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewRegistrationModal;
